import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Portal, SimpleGrid, Select, HStack, CloseButton, Button, createListCollection, Field, Input, Separator } from "@chakra-ui/react";
import { LuEraser, LuX } from "react-icons/lu";

import { useAppStore } from "../../../../../context/store";
import { useMapObjects } from "../../../../../context/MapObjectsContext";
import { resetEntityFilter } from "../../../../../utilies/Utility";
import { clearLayerFilters, filterGeojsonEntitiesByProperty } from "../../../../../utilies/filter/GeojsonFilter";

/* ---------- helpers ---------- */

const extractNestedKeys = (obj, maxDepth = 3, prefix = "") => {
  const result = [];
  if (!obj || typeof obj !== "object") return result;

  if (Array.isArray(obj)) {
    const firstObj = obj.find((el) => el && typeof el === "object");
    if (!firstObj) return result;
    return extractNestedKeys(firstObj, maxDepth, prefix);
  }

  const keys = Object.keys(obj);
  for (const key of keys) {
    const path = prefix ? `${prefix}.${key}` : key;
    result.push(path);

    if (maxDepth > 1) {
      const value = obj[key];
      if (value && typeof value === "object") {
        result.push(...extractNestedKeys(value, maxDepth - 1, path));
      }
    }
  }

  return result;
};

const getValueSafe = (prop, viewerRef) => {
  if (!prop) return prop;

  if (typeof prop.getValue === "function") {
    try {
      const time = viewerRef?.current?.clock?.currentTime;
      return prop.getValue(time);
    } catch {
      try {
        return prop.getValue();
      } catch {
        return prop;
      }
    }
  }

  return prop;
};

const getKeysFromCesiumDataSource_SchemaBased = (dataSource, viewerRef) => {
  const keys = [];
  const firstEntity = dataSource?.entities?.values?.[0];
  const props = firstEntity?.properties;
  if (!props || typeof props !== "object") return keys;

  const propNames = (props.propertyNames && Array.isArray(props.propertyNames) && props.propertyNames) || Object.keys(props || {});

  for (const name of propNames) {
    keys.push(name);

    const raw = props[name];
    const value = getValueSafe(raw, viewerRef);

    if (value && typeof value === "object") {
      keys.push(...extractNestedKeys(value, 4, name));
    }
  }

  return keys;
};

const getKeysFromGeojson_SchemaBased = (geojson) => {
  const keys = [];
  if (!geojson || typeof geojson !== "object") return keys;

  let props = null;

  if (geojson.type === "FeatureCollection" && Array.isArray(geojson.features)) {
    const first = geojson.features.find((f) => f && f.type === "Feature" && f.properties && typeof f.properties === "object");
    props = first?.properties || null;
  } else if (geojson.type === "Feature" && geojson.properties && typeof geojson.properties === "object") {
    props = geojson.properties;
  }

  if (!props) return keys;

  const propNames = Object.keys(props || {});
  for (const name of propNames) {
    keys.push(name);
    const value = props[name];
    if (value && typeof value === "object") {
      keys.push(...extractNestedKeys(value, 4, name));
    }
  }

  return keys;
};

const yieldToBrowser = () =>
  new Promise((resolve) => {
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(() => resolve(), { timeout: 30 });
    } else if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });

const hasAnyVisibleEntityFast = (dataSource, viewerRef) => {
  const entities = dataSource?.entities?.values || [];
  for (let i = 0; i < entities.length; i++) {
    const ent = entities[i];
    const s = ent?.show;
    const v = s === undefined ? true : Boolean(getValueSafe(s, viewerRef));
    if (v) return true;
  }
  return false;
};

const suspendEntityEvents = (dataSource, fn) => {
  const col = dataSource?.entities;
  if (!col || typeof col.suspendEvents !== "function" || typeof col.resumeEvents !== "function") {
    fn();
    return;
  }
  col.suspendEvents();
  try {
    fn();
  } finally {
    col.resumeEvents();
  }
};

const buildSignatureFast = (mapObjects) => {
  const arr = mapObjects || [];
  const len = arr.length;
  if (!len) return "0";
  const first = String(arr[0]?.id ?? "");
  const last = String(arr[len - 1]?.id ?? "");
  let sample = "";
  const step = Math.max(1, Math.floor(len / 10));
  for (let i = 0; i < len; i += step) {
    sample += `|${String(arr[i]?.id ?? "")}`;
    if (sample.length > 300) break;
  }
  return `${len}:${first}:${last}:${sample}`;
};

/* ---------- UI: top button ---------- */

export const GlobalGeojsonFilterButton = (props) => {
  const setFilterGeojsonOnAllDialog = useAppStore.getState().setFilterGeojsonOnAllDialog;
  return (
    <Button
      {...props}
      onClick={() => {
        setFilterGeojsonOnAllDialog(true);
        props?.onClick?.();
      }}
    >
      Filter (All Layers)
    </Button>
  );
};

/* ---------- Dialog ---------- */

export const GeojsonFilterOnAllDialog = () => {
  const filterGeojsonOnAllDialog = useAppStore((s) => s.filterGeojsonOnAllDialog);
  const setFilterGeojsonOnAllDialog = useAppStore.getState().setFilterGeojsonOnAllDialog;

  const viewerRef = useAppStore.getState().viewerRef;
  const { mapObjects, setMapObjects } = useMapObjects();

  const EMPTY_FILTER = useMemo(
    () => ({
      key: "",
      operation: "==",
      value: "",
      logicalOp: "",
    }),
    []
  );

  const operationOption = useMemo(() => [{ name: "==" }, { name: "!=" }, { name: "<" }, { name: "<=" }, { name: ">" }, { name: ">=" }], []);
  const logicalOperationOption = useMemo(() => [{ name: "AND" }, { name: "OR" }], []);

  const [labelPropertiesOption, setLabelPropertiesOption] = useState([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState([{ ...EMPTY_FILTER }]);

  const layerKeySetRef = useRef(new Map()); // layerId -> Set(keys)
  const layerHasKeyCacheRef = useRef(new Map()); // layerId -> Map(key -> boolean)

  const allKeysCacheRef = useRef({ signature: "", keys: [] });

  const snapshotRef = useRef(null); // [{ id, show, filters }]
  const globalFilterActiveRef = useRef(false);

  const getLayerKeySet = (layer) => {
    const id = layer?.id;
    if (!id) return new Set();

    const existing = layerKeySetRef.current.get(id);
    if (existing) return existing;

    const keys = [...getKeysFromCesiumDataSource_SchemaBased(layer?.dataSource, viewerRef), ...getKeysFromGeojson_SchemaBased(layer?.geojson)];

    const set = new Set(keys.filter(Boolean));
    layerKeySetRef.current.set(id, set);
    layerHasKeyCacheRef.current.set(id, new Map());
    return set;
  };

  const layerHasAnyOfKeysFast = (layer, usedKeys) => {
    const id = layer?.id;
    if (!id) return true;
    if (!usedKeys?.length) return true;

    const keySet = getLayerKeySet(layer);
    const perLayerCache = layerHasKeyCacheRef.current.get(id) || new Map();

    for (const k of usedKeys) {
      if (!k) continue;

      if (perLayerCache.has(k)) {
        if (perLayerCache.get(k)) return true;
        continue;
      }

      const ok = keySet.has(k);
      perLayerCache.set(k, ok);
      if (ok) return true;
    }

    layerHasKeyCacheRef.current.set(id, perLayerCache);
    return false;
  };

  // Build dropdown keys lazily (no pause on open)
  useEffect(() => {
    if (!filterGeojsonOnAllDialog) return;

    let cancelled = false;

    const run = async () => {
      setKeysLoading(true);

      const sig = buildSignatureFast(mapObjects);
      const cached = allKeysCacheRef.current;

      if (cached.signature === sig && Array.isArray(cached.keys) && cached.keys.length > 0) {
        if (!cancelled) {
          setLabelPropertiesOption(cached.keys.map((name) => ({ name })));
          setKeysLoading(false);
        }
        return;
      }

      const all = new Set();
      let lastYield = performance.now();

      for (let i = 0; i < (mapObjects || []).length; i++) {
        if (cancelled) return;

        const layer = mapObjects[i];
        const set = getLayerKeySet(layer);
        for (const k of set) all.add(k);

        const now = performance.now();
        if (now - lastYield > 10) {
          lastYield = now;
          await yieldToBrowser();
        }
      }

      const keys = Array.from(all).filter(Boolean);
      allKeysCacheRef.current = { signature: sig, keys };

      if (!cancelled) {
        setLabelPropertiesOption(keys.map((name) => ({ name })));
        setKeysLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterGeojsonOnAllDialog]);

  // Init filters on open (cheap)
  useEffect(() => {
    if (!filterGeojsonOnAllDialog) return;

    const firstWithFilters = (mapObjects || []).find((l) => Array.isArray(l?.filters) && l.filters.length > 0);

    if (firstWithFilters?.filters?.length) {
      const normalized = firstWithFilters.filters.map((f, index) => ({
        ...EMPTY_FILTER,
        ...f,
        key: f.key || f.key2 || f.key1 || "",
        operation: f.operation || "==",
        value: f.value || "",
        logicalOp: f.logicalOp ?? (index === 0 ? "" : "AND"),
      }));
      setFilters(normalized);
    } else {
      const idItem = (labelPropertiesOption || []).find((it) => String(it.name).toLowerCase() === "id");
      setFilters([{ ...EMPTY_FILTER, key: idItem ? idItem.name : "" }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterGeojsonOnAllDialog]);

  const updateFilter = (index, field, newValue) => {
    const updated = [...filters];
    updated[index][field] = newValue;
    setFilters(updated);
  };

  const addFilter = (logicalOp) => {
    setFilters([
      ...filters,
      {
        ...EMPTY_FILTER,
        logicalOp: logicalOp || "AND",
      },
    ]);
  };

  const removeFilter = (index) => {
    const updated = filters.filter((_, i) => i !== index);
    setFilters(updated.length > 0 ? updated : [{ ...EMPTY_FILTER }]);
  };

  const applyGlobalFilter = async () => {
    const viewer = viewerRef?.current;
    if (!viewer || busy) return;

    setBusy(true);
    try {
      const usedKeys = Array.from(new Set((filters || []).map((f) => (f?.key || "").trim()).filter(Boolean)));

      if (!globalFilterActiveRef.current || !snapshotRef.current) {
        snapshotRef.current = (mapObjects || []).map((l) => ({
          id: l?.id,
          show: l?.dataSource?.show ?? true,
          filters: Array.isArray(l?.filters) ? l.filters : [],
        }));
        globalFilterActiveRef.current = true;
      }

      const updatedMap = [...(mapObjects || [])];
      let lastYield = performance.now();

      for (let i = 0; i < updatedMap.length; i++) {
        const layer = updatedMap[i];
        const ds = layer?.dataSource;

        const hasKey = layerHasAnyOfKeysFast(layer, usedKeys);

        if (!hasKey) {
          if (ds) ds.show = false;
          updatedMap[i] = { ...layer, filters };
        } else {
          if (ds) {
            suspendEntityEvents(ds, () => {
              filterGeojsonEntitiesByProperty(viewer, ds, filters);
            });
            ds.show = hasAnyVisibleEntityFast(ds, viewerRef);
          }
          updatedMap[i] = { ...layer, filters };
        }

        const now = performance.now();
        if (now - lastYield > 10) {
          lastYield = now;
          await yieldToBrowser();
        }
      }

      setMapObjects(updatedMap);
      setFilterGeojsonOnAllDialog(false);
    } finally {
      setBusy(false);
    }
  };

  const exitGlobalFilterAndRestore = async () => {
    const viewer = viewerRef?.current;
    if (!viewer || busy) {
      setFilterGeojsonOnAllDialog(false);
      return;
    }

    setBusy(true);
    try {
      const snap = snapshotRef.current;

      let localMap = [...(mapObjects || [])];
      const localSet = (next) => {
        if (Array.isArray(next)) localMap = next;
      };

      const snapById = Array.isArray(snap) ? new Map(snap.map((s) => [s.id, s])) : new Map();

      let lastYield = performance.now();

      for (let i = 0; i < localMap.length; i++) {
        const layer = localMap[i];
        const ds = layer?.dataSource;

        if (ds) {
          suspendEntityEvents(ds, () => {
            clearLayerFilters(layer, localMap, localSet, resetEntityFilter);
          });
        } else {
          clearLayerFilters(layer, localMap, localSet, resetEntityFilter);
        }

        const s = snapById.get(layer?.id);

        if (!s) {
          if (ds) ds.show = true;
          localMap[i] = { ...layer, filters: [] };
        } else {
          if (ds) ds.show = s.show ?? true;

          const prevFilters = Array.isArray(s.filters) ? s.filters : [];
          if (prevFilters.length > 0 && ds) {
            suspendEntityEvents(ds, () => {
              filterGeojsonEntitiesByProperty(viewer, ds, prevFilters);
            });
            ds.show = hasAnyVisibleEntityFast(ds, viewerRef);
          }

          localMap[i] = { ...layer, filters: prevFilters };
        }

        const now = performance.now();
        if (now - lastYield > 10) {
          lastYield = now;
          await yieldToBrowser();
        }
      }

      setMapObjects(localMap);
      setFilters([{ ...EMPTY_FILTER }]);
      globalFilterActiveRef.current = false;
      snapshotRef.current = null;
      setFilterGeojsonOnAllDialog(false);
    } finally {
      setBusy(false);
    }
  };

  const logicalOperationCollection = useMemo(
    () =>
      createListCollection({
        items: logicalOperationOption,
        itemToString: (item) => item.name,
        itemToValue: (item) => item.name,
      }),
    [logicalOperationOption]
  );

  const keyItems = useMemo(() => {
    if (keysLoading) return [{ name: "Loading..." }];
    return labelPropertiesOption;
  }, [keysLoading, labelPropertiesOption]);

  const keyCollection = useMemo(
    () =>
      createListCollection({
        items: keyItems,
        itemToString: (item) => item.name,
        itemToValue: (item) => item.name,
      }),
    [keyItems]
  );

  const operationCollection = useMemo(
    () =>
      createListCollection({
        items: operationOption,
        itemToString: (item) => item.name,
        itemToValue: (item) => item.name,
      }),
    [operationOption]
  );

  return (
    <Dialog.Root
      lazyMount
      open={filterGeojsonOnAllDialog}
      closeOnInteractOutside={false}
      onOpenChange={(e) => setFilterGeojsonOnAllDialog(e.open)}
      trapFocus={false}
      restoreFocus={false}
      initialFocusEl={() => null}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Global Filter (All Layers)</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body pb={3}>
              {filters.map((filter, index) => (
                <SimpleGrid columns={5} gap={2} key={index} alignItems="end" templateColumns="80px 2fr 1.2fr 2fr 40px">
                  {index === 0 ? (
                    <HStack justifyContent="center" fontSize="sm">
                      <span>—</span>
                    </HStack>
                  ) : (
                    <Select.Root
                      collection={logicalOperationCollection}
                      size="sm"
                      value={[filter.logicalOp || "AND"]}
                      positioning={{ sameWidth: false }}
                      onValueChange={(value) => updateFilter(index, "logicalOp", value.value[0])}
                      disabled={busy}
                    >
                      <Select.HiddenSelect />
                      <Select.Control w="100%">
                        <Select.Trigger w="100%">
                          <HStack w="100%">{filter.logicalOp || "AND"}</HStack>
                        </Select.Trigger>
                        <Select.IndicatorGroup>
                          <Select.Indicator />
                        </Select.IndicatorGroup>
                      </Select.Control>
                      <Select.Positioner>
                        <Select.Content minW="160px" maxH="200px" overflowY="auto">
                          {logicalOperationOption.map((item) => (
                            <Select.Item key={item.name} item={item}>
                              {item.name}
                              <Select.ItemIndicator />
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Positioner>
                    </Select.Root>
                  )}

                  <Select.Root
                    collection={keyCollection}
                    size="sm"
                    value={[filter.key]}
                    positioning={{ sameWidth: false }}
                    onValueChange={(value) => updateFilter(index, "key", value.value[0])}
                    disabled={busy || keysLoading}
                  >
                    <Select.HiddenSelect />
                    <Select.Control w="100%">
                      <Select.Trigger w="100%">
                        <HStack w="100%">{filter.key || (keysLoading ? "Loading..." : "Key")}</HStack>
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content minW="220px" maxH="240px" overflowY="auto">
                        {keyItems.map((item) => (
                          <Select.Item key={item.name} item={item}>
                            {item.name}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>

                  <Select.Root
                    collection={operationCollection}
                    size="sm"
                    value={[filter.operation]}
                    positioning={{ sameWidth: false }}
                    onValueChange={(value) => updateFilter(index, "operation", value.value[0])}
                    disabled={busy}
                  >
                    <Select.HiddenSelect />
                    <Select.Control w="100%">
                      <Select.Trigger w="100%">
                        <HStack w="100%">{filter.operation}</HStack>
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content minW="120px">
                        {operationOption.map((item) => (
                          <Select.Item key={item.name} item={item}>
                            {item.name}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>

                  <Field.Root>
                    <Input height="36px" placeholder="Value" value={filter.value} onChange={(e) => updateFilter(index, "value", e.target.value)} disabled={busy} />
                  </Field.Root>

                  <Button size="sm" variant="ghost" mt={4} onClick={() => removeFilter(index)} disabled={busy}>
                    <LuX />
                  </Button>
                </SimpleGrid>
              ))}

              <Separator mt={5} />
            </Dialog.Body>

            <Dialog.Footer justifyContent={"space-between"}>
              <HStack gap={1}>
                <Button p={0} borderRadius="full" onClick={() => addFilter("AND")} textAlign={"center"} disabled={busy}>
                  AND
                </Button>
                <Button p={0} borderRadius="full" onClick={() => addFilter("OR")} textAlign={"center"} disabled={busy}>
                  OR
                </Button>
              </HStack>

              <HStack gap={2}>
                <Button onClick={exitGlobalFilterAndRestore} colorPalette={"red"} disabled={busy}>
                  (Restore) <LuEraser />
                </Button>

                <Button onClick={applyGlobalFilter} disabled={busy || keysLoading}>
                  Apply Filter
                </Button>
              </HStack>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default GeojsonFilterOnAllDialog;
